import {
  take,
  takeEvery,
  put,
  select,
  putResolve,
  call,
} from "redux-saga/effects";
import type { SagaIterator } from "redux-saga";

import { getErrorMessage } from "../../../utils/get-error-message";
import { getScheduleSubmitErrorMessage } from "../../../utils/schedule-submit-error";
import {
  beginScheduleAttempt,
  postFormAttemptContext,
  resolveScheduleAttempt,
} from "../../../api/schedule-idempotency";
import { closeModal, isModalOpened, openModal } from "../../../libs/modals";
import { Post } from "../types";
import { closePostForm } from "../../../actions/postForm";
import { ReduxRootState } from "../../../types/redux-store";
import { AccountType } from "../../../shared/types/accounts";
import {
  generateNewPostId,
  standardizationUpcomingPosts,
} from "../../../utils/posts";
import { createPostNormalizer } from "../components/create-post-normalizer";
import { PostFormState } from "../../../shared/types/post";
import { savePostForm } from "../../../sagas/postsSagas";
import axios from "axios";
import {
  LoadedDraft,
  normalizeDraft,
} from "../../../selectors/loaded-posts-normalizer";
import { fetchEditablePostCluster } from "./editable-post-cluster";
import { isFormSubmitting, postsCreatedOrUpdated } from "../post-form.model";
import { validateFull } from "../../../components/Posts/PostForm/postFormValidator";
import {
  CLOSE_ALL_POST_FORMS,
  SAVE_POST_FORM_ROUTINE,
  START_POST_FORM_ROUTINE,
  UPDATE_POST_FORM_VALIDATION,
} from "../../../constants/ActionTypes";
import { processPostFormUpdate } from "../editing/model";
import { savePost } from "../post.model";
import { POST_STATES } from "../../../constants/PostsView";
import uniq from "lodash/uniq";
import { getUser } from "../../../selectors/commonSelectors";
import { getCreateDefaultPostStatusKey } from "../../../utils/user";
import {
  addPostForm,
  BULK_CREATE_POST_MODAL,
  closeBulkCreatePostModal,
  closePostFormInBulkCreate,
  ClosePostFormInBulkCreatePayload,
  duplicatePostFormInBulkCreate,
  DuplicatePostFormPayload,
  MAX_BULK_DUPLICATE_POSTS,
  openBulkCreatePostModal,
  requestPostsFromClusterId,
  RequestPostsFromClusterId,
  requestPostsFromIds,
  RequestPostsFromIdsPayload,
  restoreDrafts,
  savePostFormInBulkCreate,
  SavePostFormPayload,
  setSaveData,
} from "./bulk-create-post.actions";
import {
  ActionOnCloseSelectedType,
  OpenBulkCreatePostModal,
  PostSavedRequestType,
} from "./bulk-create-post.types";
import {
  bulkModalNotice,
  bulkModalPendingState,
  selectedPostIdSlice,
} from "./bulk-create-post.model";
import {
  removeDraftById,
  resetDebouncingState,
  clearDraftsFromStorage,
} from "./draft-storage";
import { TakeableChannel } from "redux-saga";
import { reloadPosts } from "../../../actions/postsView";
import {
  bulkCreatePostContextManager as bm,
  BulkCreatePostActiveContext,
} from "./bulk-create-post.active-context";
import { getBulkCreateLimit } from "./limit-config";
import { shouldLazyLoadForm } from "./lazy-form-init";
import { toast } from "react-toastify";
import {
  EDIT_POST,
  EDIT_POST_MISSING_IDENTIFIER,
  POST_DUPLICATE,
} from "../../../constants/Events";
import { getPage } from "../../../selectors/commonSelectors";
import { PAGES } from "../../../constants/Pages";
import { SaveFormResult } from "../../../components/Posts/PostForm/types";
import { queryClient } from "../../../api/query-client";
import { queryKeys } from "../../../api/query-keys";
import { getMissingIdentifierTelemetryMetadata } from "../edit-telemetry";
import { selectedAccountsPostsCreatedOrUpdated } from "../../../entities/accounts/model/selected-accounts";
import { duplicatePostForCreate } from "./duplicate-post";
import { trackEventFx } from "../../../libs/analytics";
import type { User } from "../../user";

function* handlePostRequests(
  requests: OpenBulkCreatePostModal["requests"],
  context: BulkCreatePostActiveContext
) {
  if (!requests?.length) {
    return 0;
  }

  const allAccounts: Record<string, AccountType> = yield select(
    (state: ReduxRootState) => state.entities.accounts.byId
  );
  const forms: Record<string, PostFormState> = yield select(
    (state: ReduxRootState) => state.forms.postForms.byId
  );

  // Get user timezone upfront so we can dispatch SUCCESS directly.
  // Previously we dispatched TRIGGER which was handled asynchronously by a
  // forked saga (startPostForm in postsSagas). Because forked tasks run after
  // the parent saga yields, the forms weren't in Redux state when the modal
  // rendered — causing a blank modal (SKE-2931).
  const user: User | undefined = yield select(getUser);
  const userTimezone = user?.timezone;

  const currentFormCount = Object.keys(forms).length;
  const currentLimit = getBulkCreateLimit(user);

  if (currentFormCount >= currentLimit) {
    console.warn(
      `Cannot open more than ${currentLimit} post forms at once. Current count: ${currentFormCount}`
    );
    return 0;
  }

  const validRequests = requests.slice(0, currentLimit - currentFormCount);

  // Sticky inline alerts, not toasts: a toast is gone before the user can act
  // on it, and they are then left wondering why part of their input vanished.
  if (requests.length > validRequests.length) {
    yield put(
      bulkModalNotice.actions.setCapacityMessage(
        `You've reached the maximum of ${currentLimit} posts in this bulk operation. Upgrade your plan to bulk-create more.`
      )
    );
  }

  // The caller asked for accounts none of which are in the store — e.g. a saved
  // grid plan still pointing at an account that has since been removed. Without
  // this the form just opens empty and the user has no idea why.
  //
  // An empty store means "not loaded yet", not "your accounts are gone", so it
  // must not accuse a healthy account of being disconnected.
  const hasUnresolvableAccounts =
    Object.keys(allAccounts).length > 0 &&
    validRequests.some((request) => {
      const ids = extractAccountIds(request.post);
      return ids.length > 0 && ids.every((id) => !allAccounts[id as string]);
    });
  if (hasUnresolvableAccounts) {
    // Its own key, so dismissing the capacity notice cannot take this
    // explanation with it.
    yield put(
      bulkModalNotice.actions.setAccountMessage(
        "We couldn't find the account this was set up for — it may have been disconnected or removed. Choose an account to continue."
      )
    );
  }

  let lastPost: Post | undefined;
  for (const request of validRequests) {
    const accountIds = extractAccountIds(request.post);
    const accountsData = accountIds
      .map((id) => allAccounts[id as string])
      .filter(Boolean);
    let post = {
      ...request.post,
      _id:
        request.type === "create"
          ? generateNewPostId()
          : request.post._id || request.post.id,
      accountIds,
    } as Post;
    if (request.type === "create") {
      post = createPostNormalizer.makePostForCreate({
        post,
        assets: request.assets || [],
        accounts: accountsData,
        caption: undefined,
      });
    }
    if (forms[post._id]) {
      continue;
    }
    lastPost = post;
    const accountDisabled =
      request.type === "update" && request.accountDisabled;
    const isLazy = shouldLazyLoadForm(currentFormCount);
    // Dispatch SUCCESS directly instead of TRIGGER to ensure the form is
    // added to Redux state synchronously before we set selectedPostId and
    // open the modal. The TRIGGER path (bulkStartPostForm → startPostForm
    // saga → SUCCESS) was asynchronous due to redux-saga's fork scheduling,
    // which caused the modal to render before forms existed in state.
    yield put({
      type: START_POST_FORM_ROUTINE.SUCCESS,
      payload: {
        post,
        accounts: accountsData,
        requestType: request.type,
        accountSelectionDisabled: accountDisabled,
        form: {
          ...request.form,
          isLazyLoaded: isLazy,
          isInitialized: !isLazy,
        },
        requestFormTitle: request.formTitle,
        userTimezone,
      },
    });

    console.log("[PostRequest] Form created:", {
      originalPostId: request.post?._id || request.post?.id,
      newPostId: post._id,
      requestType: request.type,
      accountCount: accountsData.length,
      isLazy,
    });

    if (
      request.type === "update" &&
      request.saveBehavior?.type === "handled-by-callback"
    ) {
      context.registerFormSaveCallback(post._id, request.saveBehavior.callback);
    }
  }

  if (lastPost) {
    yield put(selectedPostIdSlice.actions.set(lastPost._id));
  }

  return validRequests.length;
}

function* bulkCreatePostModalSaga({
  payload,
}: {
  payload: OpenBulkCreatePostModal;
}) {
  const isOpened: boolean = yield select((state: ReduxRootState) =>
    isModalOpened(state, BULK_CREATE_POST_MODAL)
  );

  // Clear any stale capacity notice from a previous open. The `finally`
  // in the close path SHOULD clear it, but if the modal is force-closed
  // via an external action (or unmount outside the normal close flow),
  // the slice can keep a message that would then surface on the next
  // open before any new capacity check runs.
  if (!isOpened) {
    yield put(bulkModalNotice.actions.clear());
  }

  if (
    !isOpened &&
    payload.originalDuplicatePostsCount != null &&
    payload.originalDuplicatePostsCount > MAX_BULK_DUPLICATE_POSTS
  ) {
    // Inline alert, not a toast: a toast disappears before the user can
    // act on it and leaves no way to rediscover the limit.
    //
    // No "Upgrade your plan" CTA here: the 20-post duplicate cap is a
    // hard product limit (`MAX_BULK_DUPLICATE_POSTS` is a constant, not
    // entitlement-driven), so upgrading wouldn't help. The bulk-create
    // limit alerts elsewhere in this saga DO point to upgrade because
    // that limit IS plan-driven (`getBulkCreateLimit(user)`).
    yield put(
      bulkModalNotice.actions.setCapacityMessage(
        `You can duplicate up to ${MAX_BULK_DUPLICATE_POSTS} posts at a time. ` +
          `Showing the first ${MAX_BULK_DUPLICATE_POSTS} of ${payload.originalDuplicatePostsCount}.`
      )
    );
  }
  const context = isOpened ? bm.getActive() : bm.newActiveContext();
  // Only a fresh context gets a subscriber. Reusing the open modal's context
  // would stack a second callback — every opener passes a newly-created
  // function, so the Set cannot dedupe them — and each save would then be
  // announced twice, producing two optimistic cards for one post. The
  // subscriber registered when the modal opened is still attached and still
  // fires, so nothing is missed.
  if (!isOpened && payload.onPostSaved) {
    context.registerPostSavedCallback(payload.onPostSaved);
  }

  const createdCount: number = yield handlePostRequests(
    payload.requests,
    context
  );

  if (payload.duplicatePostsCount && createdCount > 0) {
    yield call(trackEventFx, {
      eventName: POST_DUPLICATE,
      metadata: {
        posts: Math.min(payload.duplicatePostsCount, createdCount),
      },
    });
  }

  if (isOpened) {
    return;
  }

  try {
    yield put(
      openModal({
        type: BULK_CREATE_POST_MODAL,
      })
    );

    yield take(closeBulkCreatePostModal.type);
    if (
      (!payload.postCloseBehavior ||
        payload.postCloseBehavior?.type === "auto-reload-page") &&
      context.isAnyFormSaved()
    ) {
      yield reloadCurrentPage(context);
    } else if (payload.postCloseBehavior?.type === "callback") {
      payload.postCloseBehavior.callback(context);
    }
  } catch (error) {
    console.error("Error closing bulk create post modal:", error);
  } finally {
    yield put({
      type: CLOSE_ALL_POST_FORMS,
    });
    // Reset debouncing state to prevent race conditions on modal reopen
    resetDebouncingState();
    // Clear all drafts from localStorage when bulk modal closes
    clearDraftsFromStorage();
    yield put(bulkModalPendingState.actions.setLoading(false));
    yield put(bulkModalNotice.actions.clear());
    yield put(
      closeModal({
        type: BULK_CREATE_POST_MODAL,
      })
    );
  }
}

function* reloadCurrentPage(context: BulkCreatePostActiveContext) {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.calendar.postLists,
    refetchType: "active",
  });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.calendar.draftLists,
    refetchType: "active",
  });

  const page: string = yield select(getPage);
  if (page === PAGES.CALENDAR || page === PAGES.PLANNER) {
    const newPosts = context.getAllSavedForms().map((c) => c.post);
    yield put(
      postsCreatedOrUpdated({
        post: {
          newPosts,
        },
      })
    );
    yield put(selectedAccountsPostsCreatedOrUpdated(newPosts));
    return;
  }

  if (context.hasReloadedCurrentPage()) {
    return;
  }

  yield put(reloadPosts());
  context.markCurrentPageReloaded();
}

function* addPostFormSaga({ payload }: { payload: OpenBulkCreatePostModal }) {
  yield handlePostRequests(payload.requests, bm.getActive());
}

function* formSaveSaga({ payload }: { payload: SavePostFormPayload }) {
  const { form: formResult, postSaveAction, requestType } = payload;
  const formState: PostFormState | undefined = yield select(
    (state: ReduxRootState) => state.forms.postForms.byId[formResult.post._id]
  );
  if (!formState) {
    console.error("Form state not found for post:", formResult.post._id);
    return;
  }

  yield put(isFormSubmitting.actions.startFormSubmitting());
  try {
    if (requestType === "create") {
      const createdPostIds: string[] | undefined = yield savePostForm({
        payload: { form: formResult, isControlled: true },
      });

      if (!createdPostIds) {
        return;
      }

      console.log("Post created:", createdPostIds, postSaveAction);

      // Invalidate post queries so other features (like planner) get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });

      yield completeSuccessfulSave({
        formState,
        reloadPostIds: createdPostIds,
        saveResult: formResult,
        postSaveAction,
        requestType,
      });
    }

    if (requestType === "update") {
      const [errors, warnings] = validateFull(
        formResult.newPostState,
        formResult.post,
        formResult.platformFields,
        formResult.platforms,
        formState.overlapSchedule,
        formState.userTimezone,
        formState.igPreflight
      );

      if (errors && Object.keys(errors).length > 0) {
        yield put({
          type: UPDATE_POST_FORM_VALIDATION,
          payload: { postId: formResult.post._id, errors, warnings },
        });
        yield put({
          type: SAVE_POST_FORM_ROUTINE.FAILURE,
          error: false,
          payload: "",
        });
        return;
      }

      const allAccounts: Record<string, AccountType> = yield select(
        (state: ReduxRootState) => state.entities.accounts.byId
      );

      const user: ReturnType<typeof getUser> = yield select(getUser);
      const userTimezone: string = user?.timezone || "UTC";

      const attemptContext = postFormAttemptContext(formState.post._id);
      const scheduleAttemptId = beginScheduleAttempt(attemptContext);
      const { newPost, previousPost } = yield call(processPostFormUpdate, {
        post: formState.requestedPost,
        formData: formResult,
        allAccounts,
        userTimezone,
        scheduleAttemptId,
      });

      const saveCallback = bm
        .getActive()
        .getFormSaveCallback(formState.post._id);

      if (saveCallback) {
        const posts = Array.isArray(newPost) ? newPost : [newPost];
        saveCallback({ post: posts[0], posts });
        resolveScheduleAttempt(attemptContext);
        yield closeForm(formResult.post._id, {
          actionOnLastForm: "close-bulk-modal",
        });
        return;
      }

      const result: Awaited<ReturnType<ReturnType<typeof savePost>>> =
        yield putResolve(
          savePost(newPost, previousPost, {
            creationSource: "bulk-create",
            scheduleAttemptId,
          })
        );
      resolveScheduleAttempt(attemptContext);
      console.log("Post updated:", result, postSaveAction);

      // Invalidate post queries so other features (like planner) get fresh data
      queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });

      yield completeSuccessfulSave({
        formState,
        reloadPostIds: result.ids,
        saveResult: formResult,
        postSaveAction,
        requestType,
      });
    }
  } catch (error) {
    console.error("Error saving post form:", error);

    const errorMsg =
      getScheduleSubmitErrorMessage(error) ?? getErrorMessage(error);
    if (errorMsg === "Post identifier is required") {
      yield call(trackEventFx, {
        eventName: EDIT_POST_MISSING_IDENTIFIER,
        metadata: getMissingIdentifierTelemetryMetadata({
          errorMessage: errorMsg,
          formPost: formResult.post,
          requestedPost: formState.requestedPost,
          requestType,
        }),
      });
    }
    yield put({
      type: SAVE_POST_FORM_ROUTINE.FAILURE,
      error: true,
      payload: {
        message: errorMsg,
        postId: formResult.post._id,
      },
    });
    toast.error(errorMsg);
  } finally {
    yield put(isFormSubmitting.actions.finishFormSubmitting());
  }
}

export function* completeSuccessfulSave({
  formState,
  reloadPostIds,
  saveResult,
  postSaveAction,
  requestType = "create",
}: {
  formState: PostFormState;
  reloadPostIds: string[];
  saveResult: SaveFormResult;
  postSaveAction: SavePostFormPayload["postSaveAction"];
  requestType?: PostSavedRequestType;
}): SagaIterator {
  yield call(handleSaveComplete, {
    formState,
    reloadPostIds,
    saveResult,
    requestType,
  });

  if (postSaveAction === "save-and-close") {
    yield call(closeForm, formState.post._id, {
      actionOnLastForm: "close-bulk-modal",
    });
  }
}

export function* handleSaveComplete({
  formState,
  reloadPostIds,
  saveResult,
  requestType = "create",
}: {
  formState: PostFormState;
  reloadPostIds: string[];
  saveResult: SaveFormResult;
  requestType?: PostSavedRequestType;
}): SagaIterator {
  const context = bm.getActive();
  context.notifyFormSaved(
    formState.post._id,
    saveResult,
    reloadPostIds,
    requestType
  );

  // Keep the underlying page in sync while the success screen is shown.
  // Without this, Drafts can keep rendering stale post data until a manual reload.
  yield call(reloadCurrentPage, context);

  // Clear draft for this saved form
  // For new posts, the ID changes from temp to database ID during save
  // We must clean up using the ORIGINAL temp ID, not the new database ID
  const draftIdToRemove = formState.requestedPost?._id || formState.post._id;
  removeDraftById(draftIdToRemove);

  yield put(
    setSaveData({
      id: formState.post._id,
      data: {
        postIds: reloadPostIds,
        postState: formState.postState,
      },
    })
  );
}

function* closeForm(
  id: string,
  {
    actionOnLastForm,
    actionOnCloseSelected,
  }: {
    actionOnLastForm: "close-bulk-modal" | "none";
    actionOnCloseSelected?: ActionOnCloseSelectedType;
  }
) {
  const formIds: string[] = yield select((state: ReduxRootState) =>
    Object.keys(state.forms.postForms.byId)
  );
  yield put(closePostForm(id));
  if (
    actionOnLastForm === "close-bulk-modal" &&
    formIds.length === 1 &&
    formIds[0] === id
  ) {
    yield put(closeBulkCreatePostModal());
    return;
  }

  if (!actionOnCloseSelected || actionOnCloseSelected === "ignore") {
    return;
  }

  const selectedPostId: string | undefined = yield select(
    (state: ReduxRootState) =>
      state["@features/bulk-create-post"].selectedPostId
  );

  if (selectedPostId !== id) {
    return;
  }

  switch (actionOnCloseSelected) {
    case "select-first": {
      const firstPostId = formIds.find((postId) => postId !== id);
      yield put(selectedPostIdSlice.actions.set(firstPostId));

      break;
    }
    case "select-next": {
      const curPostIdx = formIds.indexOf(id);
      const nextPostId = formIds[(curPostIdx + 1) % formIds.length];
      yield put(selectedPostIdSlice.actions.set(nextPostId));

      break;
    }
    case "select-none": {
      yield put(selectedPostIdSlice.actions.set(undefined));
      break;
    }
    // No default
  }
}
export function* closePostFormInBulkCreateSaga({
  payload,
}: {
  payload: ClosePostFormInBulkCreatePayload;
}) {
  // Clear draft before closing form
  removeDraftById(payload.postId);

  yield closeForm(payload.postId, {
    actionOnLastForm: "close-bulk-modal",
    actionOnCloseSelected: payload.actionOnCloseSelected,
  });
}

function* handleRequestPostsFormIds({
  payload,
}: {
  payload: RequestPostsFromIdsPayload;
}) {
  const { ids, state } = payload;
  yield put(bulkModalPendingState.actions.setLoading(true));
  yield put({
    type: openBulkCreatePostModal.type,
    payload: {},
  });
  try {
    const createdPosts: Post[] = yield Promise.all(
      uniq(ids).map(async (postId) => {
        if (state === POST_STATES.SCHEDULED) {
          return fetchActive(postId);
        }
        if (state === POST_STATES.QUEUED) {
          return fetchQueue(postId);
        }
        const draft = await fetchDraft(postId);
        return draft.schedule;
      })
    );

    for (const post of createdPosts) {
      yield addPostFormSaga({
        payload: {
          requests: [
            {
              type: "update",
              post: { ...post },
            },
          ],
        },
      });
    }
    yield put(selectedPostIdSlice.actions.set(ids[0]));
  } catch (error) {
    console.log(error);
  } finally {
    yield put(bulkModalPendingState.actions.setLoading(false));
  }
}

function* handleRequestPostsFormClusterId({
  payload,
}: {
  payload: RequestPostsFromClusterId;
}) {
  const { requestedPost, postCloseBehavior, trackEvent } = payload;
  const clusterId = requestedPost.postClusterId;
  const user: User | undefined = yield select(getUser);

  if (!clusterId) {
    yield put({
      type: openBulkCreatePostModal.type,
      payload: {
        requests: [{ type: "update", post: requestedPost }],
        postCloseBehavior,
      } satisfies OpenBulkCreatePostModal,
    });
    trackEvent?.({
      eventName: EDIT_POST,
      metadata: { length: 1 },
    });
    return;
  }
  yield put(bulkModalPendingState.actions.setLoading(true));
  yield put({
    type: openBulkCreatePostModal.type,
    payload: {
      requests: [],
      postCloseBehavior,
    } satisfies OpenBulkCreatePostModal,
  });
  try {
    const allResult: Awaited<ReturnType<typeof fetchEditablePostCluster>> =
      yield fetchEditablePostCluster(clusterId);

    let allFetchedPosts = allResult.posts;
    const foundPostInArchive = allResult.archives.find(
      (a) => a._id === requestedPost._id
    );

    if (
      !allFetchedPosts.some((p) => p._id === requestedPost._id) &&
      foundPostInArchive
    ) {
      allFetchedPosts = [...allFetchedPosts, foundPostInArchive];
    }

    const createdPosts = orderSelectedPostOnTop(
      allFetchedPosts,
      requestedPost._id
    );

    for (const post of createdPosts) {
      yield addPostFormSaga({
        payload: {
          requests: [
            {
              type: "update",
              post: { ...post },
            },
          ],
        },
      });
    }

    yield put(selectedPostIdSlice.actions.set(requestedPost._id));
    const currentLimit = getBulkCreateLimit(user);
    if (createdPosts.length > currentLimit) {
      // Inline alert, not a toast: a toast disappears before the user can
      // act on it and leaves no way to rediscover the limit.
      yield put(
        bulkModalNotice.actions.setCapacityMessage(
          `You've reached the maximum of ${currentLimit} posts in this bulk operation. Upgrade your plan to bulk-create more.`
        )
      );
    }
    trackEvent?.({
      eventName: EDIT_POST,
      metadata: {
        length: Math.min(createdPosts.length, currentLimit),
        "length-cluster": createdPosts.length,
      },
    });
  } catch (error) {
    console.log(error);
    yield put(bulkModalPendingState.actions.setError(`${error}`));
  } finally {
    yield put(bulkModalPendingState.actions.setLoading(false));
  }
}

export function orderSelectedPostOnTop<T extends Pick<Post, "_id">>(
  posts: T[],
  selectedId: string | undefined
) {
  const index = posts.findIndex((p) => p._id === selectedId);
  if (index === -1 || index === 0) {
    return posts;
  }

  const selectedPost = posts[index];
  const newPosts = [...posts.slice(0, index), ...posts.slice(index + 1)];
  newPosts.unshift(selectedPost);
  return newPosts;
}

export function* duplicatePostFormInBulkCreateSaga({
  payload,
}: {
  payload: DuplicatePostFormPayload;
}): SagaIterator {
  const { fromId } = payload;
  const allForms: Record<string, PostFormState> = yield select(
    (state: ReduxRootState) => state.forms.postForms.byId
  );
  const formState = allForms[fromId];
  if (!formState) {
    console.error("Form state not found for post:", fromId);
    return;
  }

  const user: ReturnType<typeof getUser> = yield select(getUser);
  const defaultPostStatusKey = getCreateDefaultPostStatusKey(user);

  const createdCount: number = yield call(
    handlePostRequests,
    [
      {
        type: "create",
        post: duplicatePostForCreate(formState.post, defaultPostStatusKey),
        form: {
          accounts: formState.accounts,
          platforms: formState.platforms,
          platformFields: formState.platformFields,
          view: formState.view,
          accountSelectionDisabled: formState.accountSelectionDisabled,
        },
      },
    ],
    bm.getActive()
  );

  if (createdCount > 0) {
    yield call(trackEventFx, {
      eventName: POST_DUPLICATE,
      metadata: { posts: createdCount },
    });
  }
}

async function fetchDraft(id: string) {
  const rs = await axios.get<LoadedDraft>(`/schedules/drafts/${id}`);
  return await normalizeDraft(rs.data);
}

async function fetchActive(id: string) {
  const rs = await axios.get<Post>(`/posts/${id}/upcoming`);
  const upcomings = await standardizationUpcomingPosts([rs.data], []);
  return upcomings[0];
}
async function fetchQueue(id: string) {
  const rs = await axios.get<Post>(`/posts/${id}/queue`);
  const upcomings = await standardizationUpcomingPosts([rs.data], []);
  return upcomings[0];
}

/**
 * Saga handler for restoring drafts from localStorage
 * Rehydrates account data from Redux and opens bulk modal with restored forms
 * Includes comprehensive error handling and validation
 */
function* handleRestoreDrafts({
  payload,
}: {
  payload: { drafts: import("./draft-storage").PostFormDraft[] };
}) {
  console.log("[DraftRestore] Saga started with drafts:", {
    count: payload.drafts?.length || 0,
    draftIds: payload.drafts?.map((d) => d.id) || [],
  });

  try {
    // IMPORTANT: Clear all old drafts BEFORE opening forms
    // Once restored, they'll be re-saved with fresh temp IDs if user makes changes
    clearDraftsFromStorage();

    const allAccounts: Record<string, AccountType> = yield select(
      (state: ReduxRootState) => state.entities.accounts.byId
    );

    const validRequests = [];
    const failedDrafts = [];

    // Validate and process each draft
    for (const draft of payload.drafts) {
      try {
        // Validate draft structure
        if (!draft.formState?.post?._id) {
          console.warn(`Draft ${draft.id}: Missing post or post._id, skipping`);
          failedDrafts.push(draft);
          continue;
        }

        // Rehydrate accounts from IDs
        const accounts =
          draft.formState.accounts
            ?.map((acc) => allAccounts[acc._id])
            .filter(Boolean) || [];

        // Validate that at least some accounts exist if there were accounts stored
        const hadAccounts = (draft.formState.accounts?.length ?? 0) > 0;
        if (hadAccounts && accounts.length === 0) {
          console.warn(
            `Draft ${draft.id}: All accounts have been disconnected, skipping`
          );
          failedDrafts.push(draft);
          continue;
        }

        // Create valid request
        // Destructure to exclude fields that are set by the reducer or handlePostRequests:
        // - requestType: Excluded from formStateRest but explicitly added back to form below
        //                to ensure it's set correctly for duplicate button visibility
        // - platforms: Computed from accounts in reducer; serialized version only has TYPE
        // - accounts: Rehydrated from Redux and passed separately
        // - post: Passed separately as request.post; a new ID is generated in handlePostRequests
        //         for "create" requests. Including post in form would overwrite the new post
        //         object (with new ID) that the reducer creates in postFormsReducer:106-120.
        const {
          requestType: _requestType,
          platforms: _platforms,
          accounts: _accounts,
          post: _post,
          ...formStateRest
        } = draft.formState;

        const requestType = draft.formState.requestType || "create";
        if (!draft.formState.requestType) {
          console.warn(
            `[DraftRestore] Draft ${draft.id}: requestType was undefined, defaulting to "create"`
          );
        }
        console.log("[DraftRestore] Creating request:", {
          draftId: draft.id,
          postId: draft.formState.post?._id,
          requestType,
          rehydratedAccounts: accounts.length,
          originalAccounts: draft.formState.accounts?.length || 0,
        });

        validRequests.push({
          type: requestType,
          post: draft.formState.post,
          form: {
            ...formStateRest,
            accounts, // Use rehydrated accounts
            requestType, // Explicitly include to ensure duplicate button works
          },
        });
      } catch (error) {
        console.error(`Failed to restore draft ${draft.id}:`, error);
        failedDrafts.push(draft);
      }
    }

    // Handle case where no drafts could be restored
    if (validRequests.length === 0) {
      toast.error(
        "Could not restore any drafts. Accounts may have been disconnected."
      );
      // Clear corrupted/invalid drafts
      clearDraftsFromStorage();
      return;
    }

    // Show warning if some drafts failed
    if (failedDrafts.length > 0) {
      toast.warning(
        `Restored ${validRequests.length} of ${payload.drafts.length} posts. Some accounts may have been disconnected.`,
        { autoClose: 5000 }
      );
    }

    // Open bulk modal with restored forms
    yield put(
      openBulkCreatePostModal({
        requests: validRequests,
        postCloseBehavior: { type: "none" },
      })
    );
  } catch (error) {
    console.error("Failed to restore drafts:", error);
    toast.error("Failed to restore drafts. Please try again.");
  }
}

export function* createBulkPostSaga() {
  console.log("[BulkSaga] createBulkPostSaga starting, listening for:", {
    openBulkCreatePostModal: openBulkCreatePostModal.type,
    restoreDrafts: restoreDrafts.type,
  });

  yield takeEvery(
    openBulkCreatePostModal.type as unknown as TakeableChannel<unknown>,
    bulkCreatePostModalSaga
  );
  yield takeEvery(
    addPostForm.type as unknown as TakeableChannel<unknown>,
    addPostFormSaga
  );
  yield takeEvery(
    savePostFormInBulkCreate.type as unknown as TakeableChannel<unknown>,
    formSaveSaga
  );
  yield takeEvery(
    closePostFormInBulkCreate.type as unknown as TakeableChannel<unknown>,
    closePostFormInBulkCreateSaga
  );
  yield takeEvery(
    requestPostsFromIds.type as unknown as TakeableChannel<unknown>,
    handleRequestPostsFormIds
  );
  yield takeEvery(
    requestPostsFromClusterId.type as unknown as TakeableChannel<unknown>,
    handleRequestPostsFormClusterId
  );
  yield takeEvery(
    duplicatePostFormInBulkCreate.type as unknown as TakeableChannel<unknown>,
    duplicatePostFormInBulkCreateSaga
  );
  yield takeEvery(
    restoreDrafts.type as unknown as TakeableChannel<unknown>,
    handleRestoreDrafts
  );
}

function extractAccountIds(post: Partial<Post> | undefined): string[] {
  if (!post) {
    return [];
  }
  if (post.accountIds?.length) {
    return post.accountIds;
  }
  if (post.accountId) {
    return [post.accountId];
  }
  return [];
}
